import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useForm } from "react-hook-form";
import { useParams, useNavigate } from "react-router-dom";
import { Button, CircularProgress, Grid } from "@mui/material";
import ProSelectField from "../../../Components/Inputs/SelectField";
import ProTextInput from "../../../Components/Inputs/TextField";
import ProCheckbox from "../../../Components/Inputs/CheckboxField";
import { STAUTES } from "../../../../utils/constant";
import {
  base64ToBlob,
  changeMode,
  changeModeForField,
  checkEditEnabled,
  downloadFileWithFileName,
  prepareDefaultValues,
  prepareInitialConfig
} from "../../../../utils/formHelper";
import ProFormsLayout from "../../../Components/ProForms/ProFormsLayout";
import {
  GET_SAA_STATES,
  GET_SPONSOR_INFORMATION,
  GET_ADMIN_INDUSTRY_TYPES,
  GET_ADMIN_JOB_CATEGORIES,
  ADMIN_RAP_INFO,
  ADMIN_RAP_INFO_APPENDIX_A,
  GET_CLIENT_JOB_TITLES,
  GET_OA_STATES
} from "../../../../utils/services/apiPath";
import { getApi, putApi } from "../../../../utils/services";
import { errorToast, successToast } from "../../../../utils/toastHelper";
import usePermission from "../../../../utils/customHooks/usePermission";
import { apprenticeRapPermissionSelector } from "../../../store/features/permissions/permissionsSlice";
import { v4 as uuidv4 } from "uuid";

export const rapInfoFormConfig = {
  externalJobTitle: {
    label: "Approved Job Title",
    perKey: "externalJobTitle",
    rules: { required: "Approved Job Title is required!" }
  },
  approvedJobTitle: {
    label: "RAP Name",
    perKey: "approvedJobTitle"
  },
  industry: {
    label: "Industry",
    perKey: "industry",
    rules: { required: "Industry is required!" }
  },
  category: {
    label: "Category",
    perKey: "category",
    rules: { required: "Category is required!" }
  },
  apprenticeshipApproach: {
    label: "Apprenticeship Approach",
    perKey: "apprenticeshipApproach"
  },
  sponsor: {
    label: "Sponsor",
    perKey: "sponsor",
    rules: { required: "Sponsor is required!" }
  },
  summary: {
    label: "Summary",
    perKey: "summary"
  },
  description: {
    label: "Description",
    perKey: "description"
  },
  rapidCode: {
    label: "RAPIDS Code",
    perKey: "rapidCode"
  },
  isDOL: {
    perKey: "isDOL",
    defaultValue: null,
    label: "Is DOL",
    rules: {
      validate: (value, values) => {
        if (values.isSSA) return true;
        return value ? true : "DOL or SAA is required!";
      }
    }
  },
  isSSA: {
    perKey: "isSSA",
    defaultValue: null,
    label: "Is SAA",
    rules: {
      validate: (value, values) => {
        if (values.isDOL) return true;
        return value ? true : "DOL or SAA is required!";
      }
    }
  },
  SSAState: {
    label: "SAA State",
    perKey: "SSAState",
    rules: { 
      required: "State is required!"
    }
  },
  programLength: {
    label: "Program Length (Years)",
    perKey: "programLength",
    rules: { required: "Program Length is required!" }
  },
  isOTJ: {
    label: "Is OTJ Track By Third Party",
    perKey: "isOTJ",
    defaultValue: null
  },
  isRTI: {
    label: "Is RTI Track By Third Party",
    perKey: "isRTI",
    defaultValue: null
  },
  programNumber: {
    label: "Program Number",
    perKey: "programNumber",
    defaultValue: null
  }
};

const ApprenticeshipApproachDropDown = [{ label: "Time-based" }, { label: "Competency-based" }, { label: "Hybrid" }];

export default function RapInfoProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState(STAUTES.IDLE);
  const [rapInfoData, setRapInfoData] = useState({});
  const [jobTitleDropDown, setJobTitleDropDown] = useState([]);
  const [industryDropDown, setIndustryDropDown] = useState([]);
  const [jobCategoryDropDown, setJobCategoryDropDown] = useState([]);
  const [ssaStateDropDown, setSsaStateDropDown] = useState([]);
  const [dolStateDropDown, setDolStateDropDown] = useState([]);
  const [apprenticeshipApproachDropDown, setApprenticeshipApproachDropDown] = useState([]);
  const [sponsorDropDown, setSponsorDropDown] = useState([]);
  const [otjHoursArray, setOtjHoursArray] = useState([]);
  const [copyOtjHoursArray, setCopyOtjHoursArray] = useState([]);
  const [originalDOLState, setOriginalDOLState] = useState(null);
  const [originalSAAState, setOriginalSAAState] = useState(null);
  const [formConfig, setFormConfig] = useState(prepareInitialConfig(rapInfoFormConfig, "read"));
  const { handleSubmit, control, setValue, watch, trigger, unregister } = useForm({
    defaultValues: prepareDefaultValues(rapInfoFormConfig)
  });
  const isEditModeOn = useMemo(() => checkEditEnabled(false, formConfig), [formConfig]);
  const [isSaving, setIsSaving] = useState(false);
  const { "Rap Info": rapInfoPermission } = usePermission(apprenticeRapPermissionSelector, "Rap Info");
  const { canView, canAdd, canEdit, canExport } = rapInfoPermission || {};
  const [isDownloadFile, setIsDownloadFile] = useState(false);

  const programLength = watch("programLength");
  const isDOL = watch("isDOL");
  const isSSA = watch("isSSA");

  const backAction = () => navigate("/apprentice-180/rap");
  const onEdit = () => {
    if (rapInfoData?.readOnly) {
      return;
    }
    setFormConfig(changeMode("edit"));
  };
  const onCancel = () => {
    setFormConfig(changeMode("read"));
    setOtjHoursArray(JSON.parse(JSON.stringify(copyOtjHoursArray)));
    setValue("programLength", copyOtjHoursArray.length);
    Object.keys(formConfig).forEach(field => setValue(field, rapInfoData[field]));
  };

  const getDetailById = useCallback(
    async rapId => {
      if (!rapId) return;
      setStatus(STAUTES.LOADING);
      const data = await getApi(`${ADMIN_RAP_INFO}/${rapId}`);
      setStatus(STAUTES.IDLE);

      const rapInfo = {
        ...data,
        isDOL: data?.isdol ?? null,
        isSSA: typeof data?.isdol === "boolean" ? !data?.isdol : data?.isssa ?? null,
        rapidCode: data?.rapidCode,
        programLength: data?.programLengthYears,
        industry: data?.industryTypeId,
        category: data?.categoryId,
        sponsor: data?.sponsorId,
        SSAState: data?.ssaState === "All States" ? null : data?.ssaStateId,
        isRTI: data?.isRtiTrackedByThirdParty,
        isOTJ: data?.isOjtTrackedByThirdParty,
        externalJobTitle: data?.clientJobTitleId
      };

      if (rapInfo.isDOL) {
        setOriginalDOLState(rapInfo.SSAState);
      } else if (rapInfo.isSSA) {
        setOriginalSAAState(rapInfo.SSAState);
      }

      if (data?.programLengthYears) {
        const otjHoursArray = Array.from({ length: data?.programLengthYears }, (_, i) => ({
          OTJHoursYear: data?.rapProgram?.otjHoursYear?.[i] || "",
          eLearningHoursYear: data?.rapProgram?.eLearningHoursYear?.[i] || "",
          id: uuidv4()
        }));
        setOtjHoursArray(otjHoursArray);
        setCopyOtjHoursArray(JSON.parse(JSON.stringify(otjHoursArray)));
        otjHoursArray.forEach((_, index) => {
          setValue(`OTJHoursYear${index + 1}`, _.OTJHoursYear || "");
          setValue(`eLearningHoursYear${index + 1}`, _.eLearningHoursYear || "");
        });
      }
      setRapInfoData(rapInfo);
      localStorage.setItem("viewingReadOnlyRapInfo", rapInfo?.readOnly);
      Object.keys(formConfig).forEach(field => setValue(field, rapInfo[field]));
    },
    [setValue]
  );

  const handleReadValueClick = name => setFormConfig(changeModeForField(name, "edit"));

  const onSubmit = async data => {
    if (isSaving) return;
    setIsSaving(true);
    setStatus(STAUTES.LOADING);
    const otj = otjHoursArray?.map(ot => parseInt(ot.OTJHoursYear));
    const eLearn = otjHoursArray?.map(ot => parseInt(ot.eLearningHoursYear));
    const payload = {
      rapInfoId: id,
      clientJobTitleGuid: data?.externalJobTitle,
      approvedJobTitle: data?.approvedJobTitle,
      industryTypeId: data?.industry,
      categoryId: data?.category,
      rapidCode: data?.rapidCode,
      summary: data?.summary,
      description: data?.description,
      programLengthYears: data?.programLength,
      isdol: data?.isDOL,
      isssa: data?.isSSA,
      ssaStateId: data?.SSAState,
      sponsorId: data?.sponsor,
      apprenticeshipApproach: data?.apprenticeshipApproach,
      rapProgram: {
        otjHoursYear: otj,
        eLearningHoursYear: eLearn
      },
      isRtiTrackedByThirdParty: data?.isRTI,
      isOjtTrackedByThirdParty: data?.isOTJ,
      programNumber: data?.programNumber
    };
    const { error } = await putApi(`${ADMIN_RAP_INFO}/${id}`, payload);
    setIsSaving(false);
    setStatus(STAUTES.IDLE);
    if (error) return errorToast(error);
    successToast(`RAP Info Updated Successfully`);
    navigate(0);
  };

  const getAllJobTitles = async () => {
    const { data, error } = await getApi(GET_CLIENT_JOB_TITLES);
    if (error) return;
    setJobTitleDropDown(data);
  };

  const getAllIndustry = async () => {
    const { data, error } = await getApi(GET_ADMIN_INDUSTRY_TYPES);
    if (error) return;
    setIndustryDropDown(data);
  };

  const getAllJobCategory = async () => {
    const { data, error } = await getApi(GET_ADMIN_JOB_CATEGORIES);
    if (error) return;
    setJobCategoryDropDown(data);
  };

  const getAllSSAStates = async () => {
    const { data, error } = await getApi(GET_SAA_STATES);
    if (error) return;
    setSsaStateDropDown(data?.filter(item => item.label !== "All States")?.map(item => ({ label: item.label, value: item.value })));
  };

  const getAllDOLStates = async () => {
    const { data, error } = await getApi(GET_OA_STATES);
    if (error) return;
    setDolStateDropDown(data?.filter(item => item.label !== "All States")?.map(item => ({ label: item.label, value: item.value })));
  };

  const getAllApprenticeshipApproachDropDown = async () => {
    setApprenticeshipApproachDropDown(ApprenticeshipApproachDropDown.map(item => ({ label: item.label, value: item.label })));
  };

  const getAllSponsorDropDown = async () => {
    const { data, error } = await getApi(GET_SPONSOR_INFORMATION);
    if (error) return;
    setSponsorDropDown(data);
  };

  const handleInputChange = (key, e, index) => {
    const { value } = e.target;
    const updatedArray = [...otjHoursArray];

    if (index >= 0 && index < updatedArray.length) {
      if (key === "OTJHoursYear" || key === "eLearningHoursYear") {
        updatedArray[index][key] = value;
        setOtjHoursArray(updatedArray);
        setValue(`${key}${index + 1}`, value);
        trigger(`${key}${index + 1}`);
      } else {
        console.error(`Invalid ${key}${index}`);
      }
    } else {
      console.error(`Index ${index} is out of bounds for otjHoursArray`);
    }
  };

  const handleDownload = async () => {
    setIsDownloadFile(true);
    try {
      const res = await getApi(`${ADMIN_RAP_INFO_APPENDIX_A}?rapInfoId=${id}`);
      const { base64Value, documentName } = res;
      if (!base64Value || !documentName) {
        throw new Error("Invalid data received from the API");
      }
      const blob = base64ToBlob(base64Value);
      const blobUrl = URL.createObjectURL(blob);
      downloadFileWithFileName(blobUrl, documentName);
      URL.revokeObjectURL(blobUrl);
      setIsDownloadFile(false);
      successToast("File downloaded successfully");
    } catch (error) {
      console.error("Download failed:", error);
      setIsDownloadFile(false);
    }
  };

  useEffect(() => {
    if (programLength) {
      setOtjHoursArray(prev => {
        if (prev.length > programLength) {
          for (let i = +programLength + 1; i <= prev.length; i++) {
            unregister(`OTJHoursYear${i}`);
            unregister(`eLearningHoursYear${i}`);
          }
          return prev.slice(0, programLength);
        }
        return Array.from({ length: programLength }, (_, index) => {
          const { OTJHoursYear = "", eLearningHoursYear = "" } = prev[index] || {};
          return { OTJHoursYear, eLearningHoursYear, id: uuidv4() };
        });
      });
    }
  }, [programLength]);

  useEffect(() => {
    getAllJobTitles();
    getAllIndustry();
    getAllJobCategory();
    getAllSSAStates();
    getAllDOLStates();
    getAllApprenticeshipApproachDropDown();
    getAllSponsorDropDown();
  }, []);

  useEffect(() => {
    getDetailById(id);
  }, [id, getDetailById]);

  useEffect(() => {
    if (typeof isDOL === "boolean") {
      setValue("isSSA", !isDOL);
      trigger("isSSA");
      
      if (isDOL) {
        setValue("SSAState", originalDOLState || null);
      } else {
        setValue("SSAState", originalSAAState || null);
      }
    }
  }, [isDOL, setValue, originalDOLState, originalSAAState]);

  useEffect(() => {
    if (typeof isSSA === "boolean") {
      setValue("isDOL", !isSSA);
      trigger("isDOL");
      
      if (isSSA) {
        setValue("SSAState", originalSAAState || null);
      } else {
        setValue("SSAState", originalDOLState || null);
      }
    }
  }, [isSSA, setValue, originalDOLState, originalSAAState]);

  const formPermission = { canView, canAdd, canEdit };
  const defaultFormProps = { control, formValues: rapInfoData, handleReadValueClick, permission: formPermission };
  const readOnly = (rapInfoData?.readOnly ? { mode: 'read' } : {});
  return (
    <ProFormsLayout
      status={status}
      hideEdit={rapInfoData?.readOnly || !canEdit}
      backAction={backAction}
      title={rapInfoData?.readOnly ? "Rap Info" : "Edit Rap Info"}
      backLabel="Go Back to Rap Info"
      beingEdited={isEditModeOn}
      onEdit={onEdit}
      onCancel={onCancel}
      onSave={handleSubmit(onSubmit)}
      isSaving={isSaving}
      disableSaveBtn={rapInfoData?.readOnly || !canEdit}
    >
      <Grid container spacing={2}>
        <ProSelectField {...defaultFormProps} {...formConfig.externalJobTitle} options={jobTitleDropDown} {...readOnly} />
        <ProTextInput {...defaultFormProps} {...formConfig.approvedJobTitle} {...readOnly}/>
        <ProTextInput {...defaultFormProps} {...formConfig.programNumber} {...readOnly}/>
        <ProSelectField {...defaultFormProps} {...formConfig.industry} options={industryDropDown} {...readOnly}/>
        <ProSelectField {...defaultFormProps} {...formConfig.category} options={jobCategoryDropDown} {...readOnly}/>
        <ProSelectField {...defaultFormProps} {...formConfig.apprenticeshipApproach} options={apprenticeshipApproachDropDown} {...readOnly}/>
        <ProSelectField {...defaultFormProps} {...formConfig.sponsor} options={sponsorDropDown} {...readOnly}/>
        <ProTextInput {...defaultFormProps} {...formConfig.summary} {...readOnly}/>
        <ProTextInput {...defaultFormProps} {...formConfig.description} {...readOnly}/>
        <ProTextInput {...defaultFormProps} {...formConfig.rapidCode} {...readOnly}/>

        <ProCheckbox
          {...defaultFormProps}
          {...formConfig.isDOL}
          handleReadValueClick={onEdit}
          disabled={rapInfoData?.readOnly || !formPermission.canEdit}
        />
        <ProCheckbox
          {...defaultFormProps}
          {...formConfig.isSSA}
          handleReadValueClick={onEdit}
          disabled={rapInfoData?.readOnly || !formPermission.canEdit}
        />
        {(isDOL || isSSA) && (
          <ProSelectField 
            {...defaultFormProps} 
            {...formConfig.SSAState} 
            label={`${isDOL ? "DOL" : "SAA"} State`}
            options={isDOL ? dolStateDropDown : ssaStateDropDown} 
            {...readOnly}
          />
        )}
        <ProTextInput {...defaultFormProps} {...formConfig.programLength} type="number" {...readOnly} />
        <ProCheckbox
          {...defaultFormProps}
          {...formConfig.isOTJ}
          handleReadValueClick={onEdit}
          disabled={rapInfoData?.readOnly || !formPermission.canEdit}
        />
        <ProCheckbox
          {...defaultFormProps}
          {...formConfig.isRTI}
          handleReadValueClick={onEdit}
          disabled={rapInfoData?.readOnly || !formPermission.canEdit}
        />

        {!!programLength &&
          otjHoursArray.map((_, index) => (
            <div key={_.id} style={{ display: "flex", width: "100%", marginBottom: "10px" }}>
              <div style={{ width: "33.3%", padding: "16px 0 0 16px" }}>
                <ProTextInput
                  size={12}
                  fieldProps={{ value: _.OTJHoursYear, onChange: e => handleInputChange("OTJHoursYear", e, index) }}
                  control={control}
                  formValues={{ [`OTJHoursYear${index + 1}`]: _.OTJHoursYear }}
                  mode={formConfig.programLength.mode}
                  name={`OTJHoursYear${index + 1}`}
                  label={`OTJ Hours Year ${index + 1}`}
                  rules={{ required: `OTJ Hours Year ${index + 1} is required!` }}
                  permission={formPermission}
                  {...readOnly}
                />
              </div>
              <div style={{ width: "33.3%", padding: "16px 0 0 16px" }}>
                <ProTextInput
                  size={12}
                  fieldProps={{ value: _.eLearningHoursYear, onChange: e => handleInputChange("eLearningHoursYear", e, index) }}
                  control={control}
                  formValues={{ [`eLearningHoursYear${index + 1}`]: _.eLearningHoursYear }}
                  mode={formConfig.programLength.mode}
                  name={`eLearningHoursYear${index + 1}`}
                  label={`Learning Hours Year ${index + 1}`}
                  rules={{ required: `Learning Hours Year ${index + 1} is required!` }}
                  permission={formPermission}
                  isSaving={isSaving}
                  {...readOnly}
                />
              </div>
            </div>
          ))}
      </Grid>
      {canExport && (
        <Button variant="contained" onClick={handleDownload} disabled={isDownloadFile}>
          {isDownloadFile ? (
            <>
              <CircularProgress size={24} />
              <span style={{ marginLeft: 10 }}>Download Appendix A Document</span>
            </>
          ) : (
            "Download Appendix A Document"
          )}
        </Button>
      )}
    </ProFormsLayout>
  );
}
